/**
 * The invoice dashboard's source-specific list endpoint (R3).
 *
 * ONE SOURCE PER REQUEST, AND THE CLIENT WILL NOT GUESS ONE. `source` is
 * mandatory and is refused locally before any request leaves, because the
 * backend has no provider default either: R2 owns the source as URL state, so
 * the answer already exists by the time anything calls this. A client that
 * quietly defaulted would turn a bookmarked link into a different list the day
 * an organisation switched provider.
 *
 * THE WHOLE ENVELOPE IS RETURNED, deliberately, unlike the older domain modules
 * in this folder. They normalise a paginated answer down to `data.items` via
 * `expectPaginatedItems`, which throws `total` away — so a page showing 25 of 68
 * matches has no way to know it is showing a third of the result set. That is
 * the R0 truncation defect. `total`, `limit`, `offset`, `source` and
 * `as_of_date` all matter to the caller: `total` is the population under the
 * current filters, and `as_of_date` is the Swiss business day every `overdue` in
 * `items` was decided against, so a footer can say which day it means.
 *
 * OPTIONAL PARAMETERS ARE OMITTED, NEVER SENT EMPTY. The endpoint's own defaults
 * then apply. This is load-bearing for `status`: the UI's "Alle" option means NO
 * status filter, and the backend answers a literal `status=all` with a 422 — so
 * "Alle" must arrive as an absent parameter, not as a word.
 *
 * THIS FILE IS A CLIENT AND NOTHING ELSE. No hook, no state, no component, no
 * derived counts. The list UI is R5 and the aggregates are R4; a helper that
 * started computing either here would be a second engine beside the backend's,
 * free to disagree with it.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  parseAdminErrorResponse,
} from "./core";

const BASE = "/api/admin/invoice-dashboard/invoices";

/** The two populations. There is no combined value, here or on the server. */
export const INVOICE_DASHBOARD_SOURCES = ["bexio", "native"];

/**
 * Build the query string.
 *
 * `source` is written first and always. Everything else is written only when it
 * carries a value — with `limit` and `offset` compared against `null`/`undefined`
 * rather than for truthiness, so an explicit `offset: 0` (page one, asked for by
 * name) still reaches the wire instead of being dropped as falsy.
 */
export function buildInvoiceDashboardQuery(params = {}) {
  const sp = new URLSearchParams();
  sp.set("source", params.source);
  if (params.status) sp.set("status", params.status);
  if (params.q) sp.set("q", params.q);
  if (params.fromDate) sp.set("from_date", params.fromDate);
  if (params.toDate) sp.set("to_date", params.toDate);
  if (params.tenantId) sp.set("tenant_id", params.tenantId);
  if (params.sort) sp.set("sort", params.sort);
  if (params.direction) sp.set("direction", params.direction);
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.offset != null) sp.set("offset", String(params.offset));
  return sp.toString();
}

/**
 * Fetch one page of ONE source.
 *
 * Returns the full envelope: `{ source, as_of_date, items, total, limit, offset }`.
 */
export async function fetchInvoiceDashboardInvoices(params = {}) {
  if (!INVOICE_DASHBOARD_SOURCES.includes(params.source)) {
    // Refused here rather than sent and rejected: the request cannot be
    // answered honestly, and a 422 round-trip would only delay saying so.
    throw new Error(
      `Ungültige Quelle für /api/admin/invoice-dashboard/invoices: ` +
        `erwartet "bexio" oder "native".`
    );
  }
  const qs = buildInvoiceDashboardQuery(params);
  const res = await fetch(`${API_BASE_URL}${BASE}?${qs}`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  const data = await res.json();
  // A malformed envelope throws rather than degrading to an empty list: a page
  // that renders "keine Rechnungen" because the shape was wrong is a lie the
  // operator has no way to detect.
  if (
    data == null ||
    typeof data !== "object" ||
    !Array.isArray(data.items) ||
    typeof data.total !== "number" ||
    typeof data.source !== "string"
  ) {
    throw new Error(
      "Ungültige Antwort von /api/admin/invoice-dashboard/invoices: " +
        "erwartet { source, as_of_date, items, total, limit, offset }."
    );
  }
  return data;
}

const SUMMARY_BASE = "/api/admin/invoice-dashboard/summary";

/**
 * Build the summary query string.
 *
 * The list's parameters MINUS everything that describes a page. `status`,
 * `sort`, `direction`, `limit` and `offset` are not part of this contract and
 * are dropped even when a caller passes them: the four cards describe the
 * population BEFORE the table's status filter, which is what makes them usable
 * as navigation. Forwarding a `status` would make each card describe itself,
 * and the backend would ignore it anyway — silently, which is worse.
 */
export function buildInvoiceDashboardSummaryQuery(params = {}) {
  const sp = new URLSearchParams();
  sp.set("source", params.source);
  if (params.q) sp.set("q", params.q);
  if (params.fromDate) sp.set("from_date", params.fromDate);
  if (params.toDate) sp.set("to_date", params.toDate);
  if (params.tenantId) sp.set("tenant_id", params.tenantId);
  return sp.toString();
}

/**
 * Fetch the four KPI cards for ONE source.
 *
 * Returns the full envelope:
 * `{ source, as_of_date, currency, total, paid, open, overdue, excluded }`.
 *
 * MONEY ARRIVES AS DECIMAL STRINGS AND STAYS THAT WAY. `"12500.00"` is not
 * parsed, rounded or reformatted here. The backend serialises money as a string
 * precisely so no float sits between the database and the screen, and a client
 * that called `Number()` on it would undo that on the first line it touched.
 * Formatting for display belongs to the component that renders it (R5).
 *
 * `excluded` is carried through whole. Those counts are the answer to "why do
 * the cards not add up to the rows I can see" — drafts, cancellations, invoices
 * Bexio no longer has, and foreign currencies that were never converted. A
 * client that dropped them would leave an operator with an unexplainable gap.
 */
export async function fetchInvoiceDashboardSummary(params = {}) {
  if (!INVOICE_DASHBOARD_SOURCES.includes(params.source)) {
    throw new Error(
      `Ungültige Quelle für /api/admin/invoice-dashboard/summary: ` +
        `erwartet "bexio" oder "native".`
    );
  }
  const qs = buildInvoiceDashboardSummaryQuery(params);
  const res = await fetch(`${API_BASE_URL}${SUMMARY_BASE}?${qs}`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  const data = await res.json();
  // A malformed envelope throws rather than degrading to zeros: a header
  // showing "CHF 0.00 offen" because the shape was wrong is a lie the operator
  // has no way to detect, and it is the most reassuring possible lie.
  const bucket = (b) =>
    b != null && typeof b === "object" && typeof b.amount === "string" &&
    typeof b.count === "number";
  if (
    data == null ||
    typeof data !== "object" ||
    typeof data.source !== "string" ||
    data.currency !== "CHF" ||
    !bucket(data.total) ||
    !bucket(data.paid) ||
    !bucket(data.open) ||
    !bucket(data.overdue) ||
    data.excluded == null ||
    typeof data.excluded !== "object" ||
    !Array.isArray(data.excluded.non_chf)
  ) {
    throw new Error(
      "Ungültige Antwort von /api/admin/invoice-dashboard/summary: " +
        "erwartet { source, as_of_date, currency, total, paid, open, " +
        "overdue, excluded }."
    );
  }
  return data;
}

const PAYMENTS_BASE = "/api/admin/invoice-dashboard/payments";

/** The two answers the payment endpoint can give about a source. */
export const INVOICE_DASHBOARD_PAYMENT_AVAILABILITY = ["available", "not_recorded"];

/**
 * Build the payment-list query string.
 *
 * A SMALLER CONTRACT THAN THE INVOICE LIST, on purpose. There is no `status` —
 * a payment has none; it is a transaction, not a document with a lifecycle — and
 * no `tenant_id`, because this slice renders no tenant control and a parameter
 * the UI cannot produce has no business being on the wire.
 *
 * The two dates bound `payment_date`: the day the money arrived, never the
 * invoice's issue or due date, and never `synced_at`, which records when we
 * copied the row.
 */
export function buildInvoiceDashboardPaymentsQuery(params = {}) {
  const sp = new URLSearchParams();
  sp.set("source", params.source);
  if (params.q) sp.set("q", params.q);
  if (params.fromDate) sp.set("from_date", params.fromDate);
  if (params.toDate) sp.set("to_date", params.toDate);
  if (params.sort) sp.set("sort", params.sort);
  if (params.direction) sp.set("direction", params.direction);
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.offset != null) sp.set("offset", String(params.offset));
  return sp.toString();
}

/**
 * Fetch one page of received payments for ONE source.
 *
 * Returns the full envelope: `{ source, availability, items, total, limit, offset }`.
 *
 * THERE IS NO `as_of_date` HERE, and requiring one would reject every correct
 * answer. The invoice list carries that field because `overdue` is a judgement
 * made against a particular Swiss business day; a payment is an event that
 * already owns its own date.
 *
 * `native` IS A LEGITIMATE SOURCE for this endpoint even though it can only ever
 * answer `not_recorded`. The alternative — refusing it here and having the
 * caller infer that answer from the source — would put a second copy of the rule
 * in the browser, free to disagree with the server's. The request costs nothing:
 * the native path issues no database query at all.
 *
 * AN UNKNOWN `availability` IS AN ERROR. A value this client does not recognise
 * would otherwise fall through every branch and paint an empty table, which is
 * the most reassuring possible way to be wrong.
 */
export async function fetchInvoiceDashboardPayments(params = {}) {
  if (!INVOICE_DASHBOARD_SOURCES.includes(params.source)) {
    throw new Error(
      `Ungültige Quelle für /api/admin/invoice-dashboard/payments: ` +
        `erwartet "bexio" oder "native".`
    );
  }
  const qs = buildInvoiceDashboardPaymentsQuery(params);
  const res = await fetch(`${API_BASE_URL}${PAYMENTS_BASE}?${qs}`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  const data = await res.json();
  if (
    data == null ||
    typeof data !== "object" ||
    typeof data.source !== "string" ||
    !INVOICE_DASHBOARD_PAYMENT_AVAILABILITY.includes(data.availability) ||
    !Array.isArray(data.items) ||
    typeof data.total !== "number"
  ) {
    throw new Error(
      "Ungültige Antwort von /api/admin/invoice-dashboard/payments: " +
        "erwartet { source, availability, items, total, limit, offset }."
    );
  }
  return data;
}
